import React, { useEffect, useRef, useState } from 'react'
import { Settings } from 'lucide-react'
import type { PlacesMapViewMode } from './PlacesMap'

/**
 * Heatmap/Clusters toggle for the PlacesMap. Two layouts in one component:
 *
 *   - `sm:` and up — segmented inline toggle.
 *   - Below `sm:` — a gear icon. The segmented toggle takes too much width
 *     on a narrow row; collapsing to a gear keeps the toolbar one line.
 *     The gear opens a tiny popover with the same two options.
 *
 * The popover closes on outside click or item selection; no focus
 * management since the options are mutually exclusive and a single tap
 * dismisses.
 */
const PlacesViewModeControl: React.FC<{
    value: PlacesMapViewMode
    onChange: (v: PlacesMapViewMode) => void
}> = ({ value, onChange }) => {
    const [menuOpen, setMenuOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!menuOpen) return
        const onDocMouseDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', onDocMouseDown)
        return () => document.removeEventListener('mousedown', onDocMouseDown)
    }, [menuOpen])

    return (
        <>
            {/* Desktop: segmented toggle */}
            <div className="hidden sm:inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
                <button
                    type="button"
                    onClick={() => onChange('heatmap')}
                    className={`px-3 py-1.5 cursor-pointer ${
                        value === 'heatmap'
                            ? 'bg-action text-plum'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    Heatmap
                </button>
                <button
                    type="button"
                    onClick={() => onChange('clusters')}
                    className={`px-3 py-1.5 border-l border-gray-300 cursor-pointer ${
                        value === 'clusters'
                            ? 'bg-action text-plum'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                    Clusters
                </button>
            </div>

            {/* Mobile: gear icon → popover */}
            <div className="relative sm:hidden" ref={ref}>
                <button
                    type="button"
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label="View options"
                    className="p-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded cursor-pointer"
                >
                    <Settings className="w-4 h-4" />
                </button>
                {menuOpen && (
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded shadow-md w-40 text-sm">
                        <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                            View
                        </div>
                        {(['heatmap', 'clusters'] as PlacesMapViewMode[]).map((opt) => (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => { onChange(opt); setMenuOpen(false) }}
                                className={`w-full text-left px-3 py-2 hover:bg-gray-50 cursor-pointer ${
                                    value === opt ? 'text-amber-800 font-medium' : 'text-gray-700'
                                }`}
                            >
                                {opt === 'heatmap' ? 'Heatmap' : 'Clusters'}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </>
    )
}

export default PlacesViewModeControl
